import { parseISO } from 'date-fns';

export const capitalize = (value: string, type = 'first'): string => {
  const sentence = value.toLowerCase();
  if (type === 'all') return sentence;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

export const removeSpecialCharacters = (str: string) => str.replace(/[^0-9.]/g, '');

export const normalizeNumber = (value: string | number | undefined, type = 'phoneNumber'): string | number | undefined => {
  if (!value) return value;
  const number = value.toString();
  if (type === 'maskless') {
    return number.replace(/\D/g, '');
  }
  return `+1 (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6, 10)}`;
};

export const generateRandomString = (length: number) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const formatMoney = (
  number: number,
  decimals?: number | null
) => number.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: decimals === null ? 2 : (decimals ?? 2),
  maximumFractionDigits: decimals === null ? 20 : (decimals ?? 2)
});

export const validateIsArray = (value: any) => Array.isArray(value);

export const toObject = (array: any[], key: string) => {
  const initialValue = {};
  return array.reduce((obj, item) => ({
    ...obj,
    [item[key]]: item,
  }), initialValue);
};

export const transformBytes = (bytes: number) => {
  const sizes = ['bytes', 'kb', 'mb', 'gb'];
  const base = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / (1024 ** base);
  return `${size.toFixed(2)} ${sizes[base]}`;
};

export const hasRole = (roles: string[] = [], role: string) => (
  roles.some((_role) => _role.includes(role))
);

export const fileExtension = (name: string, type: string) => {
  const getName = () => name.replace(/\.[^/.]+$/, '');

  const getExtension = () => name.match(/\.[^/.]+$/)![0].split('.')[1];

  switch (type) {
    case 'application/pdf':
      return { name: getName(), type: 'PDF', extension: '.pdf' };

    case 'image/png':
      return { name: getName(), type: 'PNG', extension: '.png' };

    case 'image/jpeg':
      return { name: getName(), type: 'JPG', extension: '.jpg' };

    default:
      return {
        name: getName(),
        type: getExtension().toUpperCase(),
        extension: `.${getExtension()}`,
      };
  }
};

export const formatCurrencyValueToNumber = (
  value: string | undefined,
  defaultReturn: string | number = 0,
  prefix: string | null = '$',
  separator: string | null = ','
) => {
  if (!value || value === prefix) {
    return defaultReturn;
  }

  if (prefix && separator) {
    return parseFloat(value?.split(prefix)[1].split(separator).join(''));
  }

  if (separator) {
    return parseFloat(value.split(separator).join(''));
  }

  return parseFloat(value);
};

export const formatStringInputToNumber = (value: string, returnZero: boolean = false) => {
  const parsed = parseFloat(value);

  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  if (returnZero) {
    return 0;
  }

  return null;
};

export const truncateString = (value: string | null, end: number, start = 0) => {
  if (value === null) {
    return '';
  }

  return (
    value.length > end ? value.slice(start, end).concat('...') : value
  );
};

export const IntlCurrencyFormat = (value: number, style = 'currency') => {
  const parts = value.toString().split('.');
  const decimalsAfterZero = parts[1] ? parts[1].length : 0;

  const formattedNumber = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    style,
    maximumFractionDigits: decimalsAfterZero,
    minimumFractionDigits: decimalsAfterZero
  } as any).format(value);

  return formattedNumber;
};

export const getNextIndex = (id: string, currentValues: any, values: any): any => {
  if (id !== currentValues) return { changeProperty: false };
  let nextIndex = values.map((item: any) => item.id).indexOf(id);
  nextIndex -= 1;
  if (nextIndex < 0) nextIndex = 1;
  return { shouldUpdate: true, id: values[nextIndex].id };
};

export const parseDate = (birth: string | null) => (birth ? parseISO(birth) : '');

export async function asyncForEach<T, Response>(array: T[], callback: (param: T, index?: number) => Response, delay?: number): Promise<Response[]> {
  const responseList: Response[] = [];
  for (let index = 0; index < array.length; index += 1) {
    const response = await callback(array[index], index);
    responseList.push(response);
    if (delay) await new Promise((resolve) => { setTimeout(() => { resolve({}); }, delay); });
  }

  return responseList;
}

export const getTimezoneOffsetFromString = (dateString: string) => {
  const match = dateString.match(/([+-]\d{2}:\d{2}|Z)$/);
  if (match) {
    return match[0]; // Returns "+07:00", "-05:00", or "Z"
  }
  return null;
};

export const toISO8601LocalString = (date, replaceTimeWithCurrentBrowserTime = true) => {
  if (date === null || date === undefined) return null;

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  const currentDate = replaceTimeWithCurrentBrowserTime ? new Date() : date;
  const hours = currentDate.getHours().toString().padStart(2, '0');
  const minutes = currentDate.getMinutes().toString().padStart(2, '0');
  const seconds = currentDate.getSeconds().toString().padStart(2, '0');

  const offset = date.getTimezoneOffset();
  const offsetSign = offset > 0 ? '-' : '+';
  const absOffset = Math.abs(offset);
  const offsetHours = Math.floor(absOffset / 60).toString().padStart(2, '0');
  const offsetMinutes = (absOffset % 60).toString().padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
};

export const toLocalTimeZoneStringDate = (date: string) => {
  if (date == null || date === undefined) return "";

  let dateString = date;
  const timezone = getTimezoneOffsetFromString(date);

  // Convert date to local date when the recieved date is represented on UTC
  if (timezone === "Z") {
    dateString = toISO8601LocalString(new Date(date), false) || "";
  }

  return dateString;
};

export default {};
